import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { IconChevronDown, IconCheck } from '@tabler/icons-react';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { locationService } from '@/services/location.service';
import { LocationSummaryDto } from '@/types/location';

export const ALL_LOCATIONS_VALUE = '';

interface SingleSelectProps {
  multiple?: false;
  value: string;
  onChange: (locationId: string) => void;
}

interface MultiSelectProps {
  multiple: true;
  value: string[];
  onChange: (locationIds: string[]) => void;
}

type SelectVariantProps = SingleSelectProps | MultiSelectProps;

type CommonProps = {
  showAllLocations?: boolean;
  label?: string;
  hint?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Location ids to hide from the list (e.g. already used). */
  excludeIds?: string[];
  /**
   * Externally supplied list; when provided, the component does not fetch
   * /location/switcher and instead renders this list (e.g. a role-scoped or
   * page-scoped set of locations).
   */
  locations?: LocationSummaryDto[];
  /** Loading flag used only with `locations` (external mode). */
  isLoading?: boolean;
};

export type TeeLocationSelectProps = SelectVariantProps & CommonProps;

export function TeeLocationSelect({
  showAllLocations = false,
  label,
  hint,
  placeholder,
  disabled = false,
  className,
  excludeIds,
  locations: externalLocations,
  isLoading: externalIsLoading,
  ...variantProps
}: TeeLocationSelectProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [fetchedLocations, setFetchedLocations] = useState<LocationSummaryDto[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [query, setQuery] = useState('');
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });

  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const fetchedRef = useRef(false);

  const isMulti = variantProps.multiple === true;
  const isExternal = externalLocations !== undefined;

  const fetchLocations = useCallback(async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    setIsFetching(true);
    try {
      const data = await locationService.getSwitcherLocations();
      setFetchedLocations(data);
    } catch {
      fetchedRef.current = false;
    } finally {
      setIsFetching(false);
    }
  }, []);

  useEffect(() => {
    if (isExternal) return;
    fetchLocations();
  }, [fetchLocations, isExternal]);

  const locations = externalLocations ?? fetchedLocations;
  const isLoading = isExternal ? !!externalIsLoading : isFetching;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setQuery('');
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleButtonClick = () => {
    if (disabled || isLoading) return;
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setIsOpen((prev) => {
      if (!prev) {
        setTimeout(() => searchRef.current?.focus(), 0);
      }
      return !prev;
    });
    setQuery('');
  };

  const handleSelect = (locationId: string) => {
    if (isMulti) {
      const multiProps = variantProps as MultiSelectProps;
      const current = multiProps.value;
      const next = current.includes(locationId)
        ? current.filter((id) => id !== locationId)
        : [...current, locationId];
      multiProps.onChange(next);
      // keep dropdown open for multi-select
    } else {
      (variantProps as SingleSelectProps).onChange(locationId);
      setIsOpen(false);
      setQuery('');
    }
  };

  const isSelected = (locationId: string): boolean => {
    if (isMulti) {
      return (variantProps as MultiSelectProps).value.includes(locationId);
    }
    return (variantProps as SingleSelectProps).value === locationId;
  };

  const sorted = [...locations]
    .filter((loc) => loc.status === 'ACTIVE' && !excludeIds?.includes(loc.id))
    .sort((a, b) => a.name.localeCompare(b.name));

  const filtered = sorted.filter((loc) =>
    loc.name.toLowerCase().includes(query.toLowerCase())
  );

  const allOption = showAllLocations
    ? { id: ALL_LOCATIONS_VALUE, name: t('locationSelect.allLocations') }
    : null;

  const filteredWithAll = allOption
    ? [
        ...(allOption.name.toLowerCase().includes(query.toLowerCase())
          ? [allOption]
          : []),
        ...filtered,
      ]
    : filtered;

  const getDisplayLabel = (): string => {
    if (isMulti) {
      const selected = (variantProps as MultiSelectProps).value;
      if (selected.length === 0) {
        return isLoading ? t('locationSelect.loading') : (placeholder ?? t('locationSelect.placeholder'));
      }
      if (selected.length === 1) {
        return locations.find((l) => l.id === selected[0])?.name ?? '1 location selected';
      }
      return `${selected.length} locations selected`;
    }

    const singleValue = (variantProps as SingleSelectProps).value;
    const selectedName =
      singleValue === ALL_LOCATIONS_VALUE && showAllLocations
        ? t('locationSelect.allLocations')
        : (locations.find((l) => l.id === singleValue)?.name ?? '');

    return selectedName || (isLoading ? t('locationSelect.loading') : (placeholder ?? t('locationSelect.placeholder')));
  };

  const hasValue = isMulti
    ? (variantProps as MultiSelectProps).value.length > 0
    : !!(variantProps as SingleSelectProps).value;

  return (
    <div className={cn('flex w-full flex-col gap-1.5', className)}>
      {label && (
        <label className="font-medium text-body-s text-[var(--text-content-title)]">
          {label}
        </label>
      )}

      <button
        ref={buttonRef}
        type="button"
        onClick={handleButtonClick}
        disabled={disabled || isLoading}
        className={cn(
          'w-full flex items-center justify-between px-[var(--spacing-padding-margin-s,16px)] py-[var(--spacing-padding-margin-4xs,8px)] rounded-[var(--spacing-border-radius-s,8px)] border border-[var(--border-default)] bg-[var(--background-page-content)] text-left text-body-s text-[var(--text-content-title)] transition-colors',
          'hover:bg-[var(--background-card-hover)] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ring))]',
          (disabled || isLoading) &&
            'bg-[var(--background-card-elevated)] text-[var(--text-Label-disabled,#999)] cursor-not-allowed',
          !hasValue && 'text-[var(--text-content-sub-title)]'
        )}
      >
        <span className="flex-1 truncate">{getDisplayLabel()}</span>
        <IconChevronDown
          size={16}
          className={cn(
            'text-[var(--text-content-sub-title,#6C756D)] transition-transform shrink-0 ml-2',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-50 rounded-[var(--radius-md,12px)] border border-[var(--border-default)] bg-[var(--background-surface-floating)] shadow-[0px_8px_32px_0px_rgba(0,0,0,0.5)] overflow-hidden"
            style={{
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
              width: `${menuPosition.width}px`,
            }}
          >
            <div className="p-2 border-b border-[var(--border-default)]">
              <div className="relative flex items-center">
                <Search
                  size={14}
                  className="absolute left-2.5 text-[var(--text-content-sub-title)] pointer-events-none"
                />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('locationSelect.searchPlaceholder')}
                  className="w-full pl-8 pr-3 py-1.5 text-body-s bg-[var(--background-card-elevated)] border border-[var(--border-default)] rounded-[6px] text-[var(--text-content-title)] placeholder:text-[var(--text-content-sub-title)] focus:outline-none focus:border-[var(--border-active)]"
                />
              </div>
            </div>

            <div className="overflow-y-auto max-h-[240px] py-1">
              {filteredWithAll.length === 0 ? (
                <p className="px-3 py-4 text-center text-body-s text-[var(--text-content-sub-title)]">
                  {t('locationSelect.noResults')}
                </p>
              ) : (
                filteredWithAll.map((loc) => {
                  const selected = isSelected(loc.id);
                  return (
                    <button
                      key={loc.id}
                      type="button"
                      onClick={() => handleSelect(loc.id)}
                      className={cn(
                        'w-full flex items-center justify-between px-[var(--spacing-padding-margin-s,16px)] py-[var(--spacing-padding-margin-4xs,8px)] text-body-s text-left transition-colors',
                        'hover:bg-[var(--background-card-hover)] focus:outline-none',
                        selected
                          ? 'bg-[var(--background-card-elevated)] font-semibold text-[var(--text-content-title)]'
                          : 'text-[var(--text-content-title)]'
                      )}
                    >
                      <span>{loc.name}</span>
                      {selected && (
                        <IconCheck size={14} className="text-[var(--color-text-gold)] shrink-0 ml-2" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )}

      {hint && (
        <p className="text-body-xs text-[var(--text-content-sub-title)]">{hint}</p>
      )}
    </div>
  );
}

export default TeeLocationSelect;
